import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Loader2, ShieldCheck, Plus, Trash2, GripVertical, Eye, CheckSquare, ToggleLeft, ToggleRight, Link2 } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = "/api";

/* Renders checkbox label with optional link */
function PreviewLabel({ label, linkText, linkUrl, required }) {
  if (!label) return <span className="text-[11px] text-slate-400">—</span>;
  if (linkText && linkUrl && label.includes(linkText)) {
    const parts = label.split(linkText);
    return (
      <span className="text-[11px] text-slate-500 leading-relaxed">
        {parts[0]}<a href={linkUrl} target="_blank" rel="noopener noreferrer" className="text-emerald-600 underline hover:text-emerald-700">{linkText}</a>{parts.slice(1).join(linkText)}{required ? " *" : ""}
      </span>
    );
  }
  return <span className="text-[11px] text-slate-500 leading-relaxed">{label}{required ? " *" : ""}</span>;
}

export default function PrivacyEditor() {
  const [text, setText] = useState("");
  const [linkText, setLinkText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [sections, setSections] = useState([]);
  const [checkboxes, setCheckboxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/privacy`)
      .then((r) => {
        setText(r.data.text || "");
        setLinkText(r.data.link_text || "");
        setLinkUrl(r.data.link_url || "");
        setSections(r.data.sections || []);
        setCheckboxes(r.data.checkboxes || []);
      })
      .catch(() => toast.error("Errore caricamento privacy"))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!text.trim()) { toast.error("Il testo consenso principale non puo essere vuoto"); return; }
    for (const cb of checkboxes) {
      if (!cb.label.trim()) { toast.error("Tutti i checkbox devono avere un testo"); return; }
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/privacy`, { text, link_text: linkText, link_url: linkUrl, sections, checkboxes });
      toast.success("Privacy aggiornata con successo");
    } catch { toast.error("Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const updateSection = (idx, field, value) => {
    const u = [...sections]; u[idx] = { ...u[idx], [field]: value }; setSections(u);
  };
  const addSection = () => setSections([...sections, { title: "", content: "" }]);
  const removeSection = (idx) => setSections(sections.filter((_, i) => i !== idx));

  const addCheckbox = () => setCheckboxes([...checkboxes, { label: "", required: false, link_text: "", link_url: "" }]);
  const updateCheckbox = (idx, field, value) => {
    const u = [...checkboxes]; u[idx] = { ...u[idx], [field]: value }; setCheckboxes(u);
  };
  const removeCheckbox = (idx) => setCheckboxes(checkboxes.filter((_, i) => i !== idx));

  if (loading) return <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /></div>;

  return (
    <div data-testid="privacy-editor">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Gestione Privacy</h2>
            <p className="text-xs text-slate-400">Consensi nei form, checkbox aggiuntivi e contenuto della pagina Privacy Policy.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a href="/privacy" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 px-3 py-2 rounded-lg transition-colors"
            data-testid="privacy-preview-link">
            <Eye className="w-4 h-4" /> Vedi Pagina
          </a>
          <button onClick={handleSave} disabled={saving}
            className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-full px-6 py-2.5 text-sm btn-scale flex items-center gap-2 disabled:opacity-50"
            data-testid="privacy-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salva Tutto
          </button>
        </div>
      </div>

      {/* 1. Main Consent */}
      <div className="bg-white rounded-xl border border-slate-100 p-6 mb-6">
        <h3 className="text-sm font-bold text-slate-900 mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>Consenso Principale (Obbligatorio)</h3>
        <p className="text-xs text-slate-400 mb-3">Primo checkbox nei form. Sempre obbligatorio.</p>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3}
          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm resize-none leading-relaxed focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          placeholder="Inserisci il testo del consenso privacy principale..." data-testid="privacy-consent-input" />
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="text-[11px] font-semibold text-slate-500 mb-1 block"><Link2 className="w-3 h-3 inline mr-1 -mt-0.5" />Testo del link (parola nel testo sopra)</label>
            <Input value={linkText} onChange={(e) => setLinkText(e.target.value)} className="h-9 rounded-lg text-sm"
              placeholder='es: Privacy Policy' data-testid="privacy-main-link-text" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 mb-1 block">URL di destinazione</label>
            <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} className="h-9 rounded-lg text-sm"
              placeholder='es: /privacy' data-testid="privacy-main-link-url" />
          </div>
        </div>
        <p className="text-[10px] text-slate-400 mt-1.5">Scrivi nel testo la parola esatta che vuoi rendere cliccabile, poi indicala qui sopra con il suo URL.</p>
        <div className="bg-slate-50 rounded-lg p-3 mt-3">
          <p className="text-[10px] font-semibold text-slate-400 mb-1.5">Anteprima:</p>
          <label className="flex items-start gap-2.5">
            <input type="checkbox" checked readOnly className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300 text-emerald-600" />
            <PreviewLabel label={text} linkText={linkText} linkUrl={linkUrl} required />
          </label>
        </div>
      </div>

      {/* 2. Additional Checkboxes */}
      <div className="bg-white rounded-xl border border-slate-100 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              <CheckSquare className="w-4 h-4 inline mr-1.5 text-indigo-500 -mt-0.5" />
              Checkbox Aggiuntivi ({checkboxes.length})
            </h3>
            <p className="text-xs text-slate-400">Consensi extra sotto quello principale. Ogni checkbox puo avere un link cliccabile.</p>
          </div>
          <button onClick={addCheckbox}
            className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg transition-colors"
            data-testid="privacy-add-checkbox">
            <Plus className="w-3.5 h-3.5" /> Aggiungi Checkbox
          </button>
        </div>

        {checkboxes.length === 0 ? (
          <div className="text-center py-6 border border-dashed border-slate-200 rounded-xl">
            <CheckSquare className="w-5 h-5 text-slate-300 mx-auto mb-2" />
            <p className="text-xs text-slate-400 mb-1">Nessun checkbox aggiuntivo</p>
            <button onClick={addCheckbox} className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">+ Aggiungi il primo</button>
          </div>
        ) : (
          <div className="space-y-4">
            {checkboxes.map((cb, idx) => (
              <div key={idx} className="border border-slate-100 rounded-xl p-4 hover:border-slate-200 transition-colors" data-testid={`privacy-checkbox-${idx}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 space-y-2.5">
                    <textarea value={cb.label} onChange={(e) => updateCheckbox(idx, "label", e.target.value)} rows={2}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm resize-none leading-relaxed focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      placeholder="Testo del checkbox..."
                      data-testid={`privacy-checkbox-label-${idx}`} />
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] font-semibold text-slate-400 mb-0.5 block"><Link2 className="w-3 h-3 inline mr-0.5 -mt-0.5" />Testo link</label>
                        <Input value={cb.link_text || ""} onChange={(e) => updateCheckbox(idx, "link_text", e.target.value)}
                          className="h-8 rounded-lg text-xs" placeholder='es: Privacy Policy' data-testid={`privacy-checkbox-link-text-${idx}`} />
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold text-slate-400 mb-0.5 block">URL</label>
                        <Input value={cb.link_url || ""} onChange={(e) => updateCheckbox(idx, "link_url", e.target.value)}
                          className="h-8 rounded-lg text-xs" placeholder='es: /privacy' data-testid={`privacy-checkbox-link-url-${idx}`} />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => updateCheckbox(idx, "required", !cb.required)}
                        className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full transition-colors ${cb.required ? "bg-amber-50 text-amber-700" : "bg-slate-50 text-slate-400 hover:text-slate-600"}`}
                        data-testid={`privacy-checkbox-required-${idx}`}>
                        {cb.required ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                        {cb.required ? "Obbligatorio" : "Facoltativo"}
                      </button>
                    </div>
                  </div>
                  <button onClick={() => removeCheckbox(idx)}
                    className="p-2 text-slate-300 hover:text-red-500 transition-colors shrink-0"
                    data-testid={`privacy-checkbox-remove-${idx}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {checkboxes.length > 0 && (
          <div className="bg-slate-50 rounded-lg p-3 mt-4">
            <p className="text-[10px] font-semibold text-slate-400 mb-2">Anteprima nel form:</p>
            <div className="space-y-2">
              <label className="flex items-start gap-2.5">
                <input type="checkbox" checked readOnly className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300 text-emerald-600" />
                <PreviewLabel label={text} linkText={linkText} linkUrl={linkUrl} required />
              </label>
              {checkboxes.map((cb, idx) => (
                <label key={idx} className="flex items-start gap-2.5">
                  <input type="checkbox" readOnly className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300 text-emerald-600" />
                  <PreviewLabel label={cb.label} linkText={cb.link_text} linkUrl={cb.link_url} required={cb.required} />
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 3. Privacy Policy Page Sections */}
      <div className="bg-white rounded-xl border border-slate-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Pagina Privacy Policy ({sections.length} sezioni)</h3>
            <p className="text-xs text-slate-400">Contenuto della pagina pubblica /privacy visibile ai clienti.</p>
          </div>
          <button onClick={addSection}
            className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 hover:text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-lg transition-colors"
            data-testid="privacy-add-section">
            <Plus className="w-3.5 h-3.5" /> Aggiungi Sezione
          </button>
        </div>

        <div className="space-y-4">
          {sections.map((section, idx) => (
            <div key={idx} className="border border-slate-100 rounded-xl p-4 hover:border-slate-200 transition-colors" data-testid={`privacy-section-${idx}`}>
              <div className="flex items-start gap-3">
                <GripVertical className="w-4 h-4 text-slate-300 mt-2.5 shrink-0" />
                <div className="flex-1 space-y-2">
                  <Input value={section.title} onChange={(e) => updateSection(idx, "title", e.target.value)}
                    className="h-9 rounded-lg text-sm font-semibold" placeholder="Titolo sezione"
                    data-testid={`privacy-section-title-${idx}`} />
                  <textarea value={section.content} onChange={(e) => updateSection(idx, "content", e.target.value)} rows={4}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm resize-none leading-relaxed focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    placeholder="Contenuto della sezione... (usa - per elenchi puntati)"
                    data-testid={`privacy-section-content-${idx}`} />
                </div>
                <button onClick={() => removeSection(idx)} className="p-2 text-slate-300 hover:text-red-500 transition-colors shrink-0"
                  data-testid={`privacy-remove-section-${idx}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {sections.length === 0 && (
          <div className="text-center py-8">
            <p className="text-sm text-slate-400 mb-2">Nessuna sezione.</p>
            <button onClick={addSection} className="text-sm font-semibold text-emerald-600 hover:text-emerald-700">+ Aggiungi Sezione</button>
          </div>
        )}
      </div>
    </div>
  );
}
